import { getWordsCount } from '~/helpers/functions';
import useUserCountry from '~/hooks/useUserCountry';
import { getDetectorLoadingTime } from '~/modules/tools/views/AiDetectorV4/helpers';
import {
  DETECTION_EDGE,
  DETECTION_STAGE,
} from '~/modules/tools/views/PlagiarismCheckerV4/constants';
import { PlagiarismExecutionType } from '~/modules/tools/views/PlagiarismCheckerV4/types';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { isLowerTierCountry } from '~/utils/common';

export const getDetectionStage = (result: number): DETECTION_STAGE => {
  const stage = Object.entries(DETECTION_EDGE)
    .sort(([, a], [, b]) => b - a)
    .find(([, value]) => result >= value);
  return stage ? (stage[0] as DETECTION_STAGE) : DETECTION_STAGE.LOW_LEVEL;
};

export const getPlagiarismLoadingTime = (wordsCount: number): number => {
  const MIN_WORDS = 200;
  const MIN_TIME = 30000; // 30 sec
  const RESPONSE_COEFFICIENT = 25.8;
  const EDGE_THRESHOLD = -102;

  if (wordsCount < MIN_WORDS) {
    return MIN_TIME;
  }

  const calculatedTime =
    EDGE_THRESHOLD + RESPONSE_COEFFICIENT * Math.log(wordsCount);

  return Math.round(calculatedTime) * 1000; // return result in milliseconds
};

export const calculatePlagiarismLoadingTime = (data: {
  requestText: string;
  requestLang: string;
  isDetectorIncluded: boolean;
}): number => {
  const plagiarismTime = getPlagiarismLoadingTime(
    getWordsCount(data.requestText),
  );

  if (data.isDetectorIncluded) {
    const detectorTime = getDetectorLoadingTime(
      data.requestText,
      data.requestLang,
    );

    return Math.max(detectorTime, plagiarismTime);
  }

  return plagiarismTime;
};

export const getPlagiarismResult = (res: PlagiarismExecutionType): number => {
  const MAX_PLAGIARISM_RESULT = 100;

  const { identical_words, minor_changed_words, omitted_words } = res;

  const plagiarismCalculation =
    identical_words + minor_changed_words + omitted_words;

  return plagiarismCalculation > MAX_PLAGIARISM_RESULT
    ? MAX_PLAGIARISM_RESULT
    : plagiarismCalculation;
};

export const getPlagiarismWordsBalance = (): number => {
  const profile = useAppSelector((state) => state.profile);
  const userCountryCode = useUserCountry(profile.country);
  const isLowerTierCountryUser = isLowerTierCountry(userCountryCode);

  const BALANCE = {
    optimized: 30000,
    lowerTier: 15000,
  };

  if (isLowerTierCountryUser) return BALANCE.lowerTier;

  return BALANCE.optimized;
};

export const getPlagiarismInputLimits = (data: {
  isAnnualSub: boolean;
  isNotPremium: boolean;
}): { minCount: number; maxCount: number } => {
  const { isAnnualSub, isNotPremium } = data;

  const LIMITS = {
    // words
    standard_min: 10,
    optimized_monthly_max: 15000,
    optimized_annual_max: 50000,
    not_premium_max: 15000,
  };

  const min = LIMITS.standard_min;

  const max = isNotPremium
    ? LIMITS.not_premium_max
    : isAnnualSub
      ? LIMITS.optimized_annual_max
      : LIMITS.optimized_monthly_max;

  return {
    minCount: min,
    maxCount: max,
  };
};
